/**
 * MyToolbar Component
 *
 * Custom form toolbar with responsive Save and Cancel buttons.
 * Features:
 * - Sticky positioning on mobile for better UX
 * - Responsive button sizes
 * - Optional delete button
 * - Consistent styling across the app
 *
 * USAGE:
 * ```tsx
 * <SimpleForm toolbar={<MyToolbar resource="examples" />}>
 *   ...
 * </SimpleForm>
 * ```
 */

import React from 'react';
import {
  Toolbar,
  SaveButton,
  DeleteButton,
  useTranslate,
  useRedirect,
  ToolbarProps,
} from 'react-admin';
import { Box, Button, useTheme, useMediaQuery } from '@mui/material';
import { Close as CloseIcon, Save as SaveIcon } from '@mui/icons-material';
import { useDialogForm } from './DialogFormContext';

const actionButtonSx = (isMobile: boolean) => ({
  textTransform: 'none' as const,
  fontWeight: 600,
  minWidth: isMobile ? 0 : 120,
  ...(isMobile && {
    flex: 1,
    fontSize: '0.875rem',
    px: 1,
  }),
});

interface MyToolbarProps extends ToolbarProps {
  onCancel?: () => void;
  resource?: string;
  showDelete?: boolean;
  onDelete?: () => void;
  deleteButton?: boolean;
  saveDisabled?: boolean;
}

const MyToolbar: React.FC<MyToolbarProps> = ({
  onCancel,
  resource = '',
  showDelete = false,
  onDelete,
  deleteButton = false,
  saveDisabled = false,
  ...rest
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const translate = useTranslate();
  const redirect = useRedirect();
  const dialogCtx = useDialogForm();

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else if (dialogCtx) {
      dialogCtx.onClose();
    } else {
      redirect('list', resource);
    }
  };

  const hasDelete = showDelete || deleteButton;

  return (
    <Toolbar
      {...rest}
      sx={{
        marginTop: 'auto',
        position: 'sticky',
        bottom: 0,
        display: 'flex',
        justifyContent: hasDelete ? 'space-between' : 'flex-end',
        alignItems: 'center',
        gap: { xs: 1, sm: 2 },
        width: '100%',
        py: isMobile ? 1.5 : 2,
        px: isMobile ? 1.5 : 3,
        backgroundColor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.divider}`,
        zIndex: 1100,
        ...(isMobile && {
          position: 'fixed',
          left: 0,
          right: 0,
          boxShadow: `0 -4px 12px ${theme.palette.action.hover}`,
        }),
      }}
    >
      {(showDelete || deleteButton) && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            ...(isMobile && { flex: '0 0 auto' }),
          }}
        >
          {onDelete ? (
            <Button
              variant="outlined"
              color="error"
              onClick={onDelete}
              size={isMobile ? 'small' : 'medium'}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                minWidth: isMobile ? 0 : 100,
                ...(isMobile && { fontSize: '0.875rem', px: 1 }),
              }}
            >
              {translate('ra.action.delete')}
            </Button>
          ) : (
            <DeleteButton
              size={isMobile ? 'small' : 'medium'}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                ...(isMobile && { fontSize: '0.875rem' }),
              }}
            />
          )}
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          ...(isMobile && { flex: 1, justifyContent: 'stretch' }),
        }}
      >
        <Button
          variant="contained"
          color="primary"
          startIcon={<CloseIcon />}
          onClick={handleCancel}
          size={isMobile ? 'small' : 'medium'}
          sx={actionButtonSx(isMobile)}
        >
          {translate('ra.action.cancel')}
        </Button>

        <SaveButton
          variant="contained"
          color="primary"
          size={isMobile ? 'small' : 'medium'}
          icon={<SaveIcon />}
          sx={actionButtonSx(isMobile)}
          disabled={saveDisabled}
        />
      </Box>
    </Toolbar>
  );
};

export default MyToolbar;
